import {useState} from 'react';
import {Check, Lock} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@kroll-resolver-crisp/ui-components-react';
import type {TaxonomyChange} from '../../api/workflows';
import {TaxonomyChanges} from './TaxonomyChanges';
import {
  entryLabel,
  short,
  type GroundingEntry,
  type PendingArchive,
  type PendingConfig,
  type PendingGround
} from './workflowUi';

type Kind = 'config' | 'ground' | 'archive';
type Decision = 'approve' | 'reject';

type Row = {
  change: 'added' | 'changed' | 'retired';
  kind: string;
  content: string;
  concept: string;
};

function groundingRows(pending: PendingGround): Row[] {
  const {entries, previous} = pending.replacement;
  const rows: Row[] = [];
  for (const entry of entries) {
    const before = entry.entry_id ? previous.find((p) => p.id === entry.entry_id) : undefined;
    if (!entry.entry_id || !before) {
      rows.push({
        change: 'added',
        kind: entryLabel(entry),
        content: entry.content,
        concept: entry.path
      });
    } else if (
      before.content !== entry.content ||
      entryLabel(before) !== entryLabel(entry) ||
      before.concept_id !== entry.concept_id
    ) {
      rows.push({
        change: 'changed',
        kind: entryLabel(entry),
        content: entry.content,
        concept: entry.path
      });
    }
  }

  for (const before of previous) {
    if (!entries.some((entry) => entry.entry_id === before.id)) {
      rows.push({
        change: 'retired',
        kind: entryLabel(before),
        content: before.content,
        concept: ''
      });
    }
  }

  return rows;
}

function Tiles({rows}: {readonly rows: Row[]}) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {(['added', 'changed', 'retired'] as const).map((change) => (
        <div key={change} className="border-border bg-card flex flex-col rounded-lg border p-3">
          <span className="text-xl font-semibold tabular-nums">
            {rows.filter((row) => row.change === change).length}
          </span>
          <span className="text-muted-foreground text-xs capitalize">{change}</span>
        </div>
      ))}
    </div>
  );
}

export function ReviewPanel({
  workflowName,
  hasProposal,
  pendingConfig,
  pendingGround,
  pendingArchive,
  currentEntries,
  rootPath,
  taxonomyChanges,
  canReview,
  canDecide,
  viewer,
  busy,
  onDecide
}: {
  readonly workflowName: string;
  readonly hasProposal: boolean;
  readonly pendingConfig: PendingConfig | null;
  readonly pendingGround: PendingGround | null;
  readonly pendingArchive: PendingArchive | null;
  readonly currentEntries: GroundingEntry[];
  readonly rootPath: (id: string) => string;
  readonly taxonomyChanges: readonly TaxonomyChange[];
  readonly canReview: boolean;
  readonly canDecide: (author: string) => boolean;
  readonly viewer: string;
  readonly busy: boolean;
  readonly onDecide: (kind: Kind, decision: Decision) => void;
}) {
  const [ask, setAsk] = useState<{kind: Kind; decision: Decision} | null>(null);

  const kind: Kind | null = pendingArchive
    ? 'archive'
    : pendingConfig
      ? 'config'
      : pendingGround
        ? 'ground'
        : null;

  if (!kind) {
    return (
      <Empty className="border-border border p-6">
        <EmptyHeader>
          <EmptyTitle>Nothing to review</EmptyTitle>
          <EmptyDescription>
            {hasProposal && !canReview
              ? 'A proposal is waiting for an approver.'
              : 'Configuration and grounding proposals appear here until an approver decides.'}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  const author = (pendingArchive?.author ?? pendingConfig?.author ?? pendingGround?.author) || '';
  const mine = author !== '' && author.toLowerCase() === viewer.toLowerCase();
  const by = mine ? 'you' : author;
  const labels: Record<Kind, string> = {
    config: 'Approve and publish',
    ground: 'Approve replacement',
    archive: 'Approve archive'
  };

  let heading = 'Grounding replacement';
  let sub: string | null = null;
  let body: React.ReactNode = null;

  if (kind === 'archive') {
    heading = 'Archive guidance';
    sub = `Submitted by ${by}`;
    body = (
      <p className="text-sm">
        Approving stops RD routing to <b>{workflowName}</b> and makes it read-only. Its
        configuration, {currentEntries.length} grounding entries, revisions and audit history are
        kept.
      </p>
    );
  } else if (kind === 'config' && pendingConfig) {
    const config = pendingConfig.configuration;
    const first = config.base_revision === null;
    heading = first ? 'First approval' : 'Configuration change';
    sub = `Submitted by ${by} · against revision ${short(pendingConfig.base_revision)}`;
    const fields: Array<[string, string]> = [
      ['Name', config.name],
      [
        config.root_concept_ids.length === 1 ? 'Root concept' : 'Root concepts',
        config.root_concept_ids.map(rootPath).join('; ')
      ],
      ['Routing definition', config.routing_definition || '—'],
      ['Routing keywords', config.routing_keywords || '—'],
      ['Enabled', config.enabled ? 'Yes' : 'No'],
      ['Always offer Non-Violative', config.include_non_violative ? 'Yes' : 'No'],
      ['Taxonomy pack', config.pack_version]
    ];
    body = (
      <>
        <div className="border-border overflow-x-auto rounded border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-40">Field</TableHead>
                <TableHead>Proposed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map(([field, value]) => (
                <TableRow key={field}>
                  <TableCell className="align-top font-medium">{field}</TableCell>
                  <TableCell className="whitespace-pre-wrap">{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {!first && taxonomyChanges.length > 0 ? (
          <section aria-label="Taxonomy changes" className="space-y-2">
            <h4 className="text-sm font-semibold">Taxonomy changes since the served revision</h4>
            <TaxonomyChanges changes={taxonomyChanges} />
          </section>
        ) : null}
      </>
    );
  } else if (pendingGround) {
    const rows = groundingRows(pendingGround);
    sub = `Submitted by ${by} · ${pendingGround.replacement.entries.length} proposed entries`;
    body = (
      <>
        <Tiles rows={rows} />
        <div className="border-border overflow-x-auto rounded border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">Change</TableHead>
                <TableHead className="w-32">Kind</TableHead>
                <TableHead>Content</TableHead>
                <TableHead className="w-1/4">Concept</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.change}:${row.kind}:${row.content}`}>
                  <TableCell>
                    <Badge variant={row.change === 'retired' ? 'destructive' : 'secondary'}>
                      {row.change}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{row.kind}</Badge>
                  </TableCell>
                  <TableCell
                    className={`whitespace-pre-wrap ${row.change === 'retired' ? 'text-muted-foreground line-through' : ''}`}>
                    {row.content}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">{row.concept}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </>
    );
  }

  const allowed = canReview && canDecide(author);
  const destructive = ask?.kind === 'archive' && ask.decision === 'approve';
  const effect =
    kind === 'archive'
      ? 'RD stops routing to it on its next load. Its revisions and history are kept.'
      : kind === 'ground'
        ? 'RD starts serving the new grounding on its next load. Retired entries stop being sent to the classifier.'
        : 'RD routes against the new revision on its next load.';
  const selfApproval = mine
    ? 'You submitted this, so approving it yourself skips the independent review. '
    : '';

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">{heading}</h3>
          {sub ? <p className="text-muted-foreground text-xs">{sub}</p> : null}
        </div>
        {allowed ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {mine ? (
              <span className="text-muted-foreground text-sm">
                You submitted this. Another approver can publish it.
              </span>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                setAsk({kind, decision: 'reject'});
              }}>
              {mine ? 'Withdraw' : 'Reject'}
            </Button>
            <Button
              size="sm"
              variant={kind === 'archive' ? 'destructive' : mine ? 'outline' : 'default'}
              disabled={busy}
              onClick={() => {
                setAsk({kind, decision: 'approve'});
              }}>
              <Check aria-hidden className="size-4" />
              {mine ? 'Approve my own change' : labels[kind]}
            </Button>
          </div>
        ) : (
          <span className="text-muted-foreground inline-flex items-center gap-1 text-sm">
            <Lock aria-hidden className="size-3" />
            {mine
              ? 'You submitted this. Waiting for another approver.'
              : canReview
                ? 'Waiting for an independent approver.'
                : 'Waiting for an approver'}
          </span>
        )}
      </div>
      {body}
      <AlertDialog
        open={ask !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAsk(null);
          }
        }}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {ask?.decision === 'reject'
                ? mine
                  ? 'Withdraw this proposal?'
                  : 'Reject this proposal?'
                : kind === 'archive'
                  ? `Archive ${workflowName}?`
                  : kind === 'ground'
                    ? 'Publish this grounding replacement?'
                    : 'Approve this configuration?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {ask?.decision === 'reject'
                ? mine
                  ? 'Nothing that RD serves changes. You can submit a new proposal.'
                  : 'Nothing that RD serves changes. The author can submit a new proposal.'
                : selfApproval + effect}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant={destructive || ask?.decision === 'reject' ? 'destructive' : 'default'}
              onClick={() => {
                if (ask) {
                  onDecide(ask.kind, ask.decision);
                }

                setAsk(null);
              }}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
